<script setup>
import {
    convertKmAsPerTrackUnits,
    formatSpeedUnitsHuman,
    formatUnitsHuman,
    getDeviceNavigationAppUrl,
    round,
    toEventLocalISOString,
} from "@/Helpers/helperFunctions";
import { ArrowTopRightOnSquareIcon } from "@heroicons/vue/24/solid";
import TooltipIcon from "@/Components/TooltipIcon.vue";
import { DateTime } from "luxon";
import { Duration } from "luxon";
import HeadingComponent from "@/Components/HeadingComponent.vue";
import FwbTooltip from "@/Components/FwbTooltip/FwbTooltip.vue";
import SpinnerIcon from "@/Components/assets/SpinnerIcon.vue";
import NavigateByBadge from "@/Components/NavigateByBadge.vue";

defineProps({
    event: {
        type: Object,
    },
});

function isClassSectionProcessing(classSection) {
    const trackVersion = classSection.trackVersion;

    return (
        !trackVersion ||
        trackVersion.is_processing ||
        !trackVersion.start_location ||
        !trackVersion.end_location
    );
}

function isClassProcessing(eventClass) {
    return eventClass.classSections.some(isClassSectionProcessing);
}

function getTotalDistanceInClass(eventClass) {
    let totalDistance = 0;
    for (let i = 0, l = eventClass.classSections.length; i < l; i++) {
        totalDistance += eventClass.classSections[i].trackVersion?.distance ?? 0;
    }

    return totalDistance / 1000;
}

function getTotalDistanceInClassUntilTrack(eventClass, t) {
    if (eventClass.classSections.length === 1) {
        return "";
    }

    let totalDistance = 0;

    for (let i = 0, l = eventClass.classSections.length; i < l; i++) {
        totalDistance += eventClass.classSections[i].trackVersion.distance;

        if (eventClass.classSections[i].id === t.id) {
            return `(${convertKmAsPerTrackUnits(
                totalDistance / 1000,
                eventClass.classSections[i].track.units,
                0,
            )} ${formatUnitsHuman(eventClass.classSections[i].track.units)} / ${convertKmAsPerTrackUnits(
                getTotalDistanceInClass(eventClass),
                eventClass.classSections[i].track.units,
                0,
            )} ${formatUnitsHuman(eventClass.classSections[i].track.units)})`;
        }
    }
    return "";
}

function containsRoadbookSections(classSections) {
    let containsAtLeastOneRoadbook = false;
    for (let i = 0, l = classSections.length; i < l; i++) {
        if (classSections[i].track.navigate_by === "rb") {
            containsAtLeastOneRoadbook = true;
            break;
        }
    }

    return containsAtLeastOneRoadbook;
}
</script>

<template>
    <div class="space-y-4">
        <HeadingComponent
            v-if="event.classes.length === 0"
            class="rounded-lg bg-white p-4 shadow-lg sm:px-6"
        >
            <template #name>
                The itinerary for this event has not yet been created by the event organizer.
            </template>
        </HeadingComponent>

        <div
            v-for="(eventClass, eventClassIndex) in event.classes"
            :key="eventClassIndex"
            class="flex flex-col rounded-lg bg-white shadow-lg lg:flex-row"
        >
            <div class="max-w-xs flex-1 border-t border-gray-100">
                <dl class="divide-y divide-gray-100">
                    <div class="p-4 sm:grid sm:grid-cols-1 sm:gap-4 sm:px-6">
                        <h3 class="text-base font-semibold leading-7 text-gray-900">
                            {{ eventClass.name }}
                        </h3>
                        <dd v-if="eventClass.classSections.length === 0">
                            The itinerary for this class has not yet been created by the event organizer.
                        </dd>
                    </div>
                    <div
                        v-if="eventClass.classSections.length > 0"
                        class="p-4 sm:grid sm:grid-cols-1 sm:gap-4 sm:px-6"
                    >
                        <dt class="text-sm font-medium text-gray-900">Total distance</dt>
                        <dd
                            v-if="isClassProcessing(eventClass)"
                            class="mt-1 inline-flex items-center gap-x-2 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0"
                        >
                            <SpinnerIcon class="size-4" />
                            processing...
                        </dd>
                        <dd
                            v-else
                            class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                        >
                            {{
                                convertKmAsPerTrackUnits(
                                    getTotalDistanceInClass(eventClass),
                                    eventClass.classSections[0].track.units,
                                    0,
                                )
                            }}
                            {{ formatUnitsHuman(eventClass.classSections[0].track.units) }}
                        </dd>
                    </div>
                    <div
                        v-if="containsRoadbookSections(eventClass.classSections)"
                        class="p-4 sm:grid sm:grid-cols-1 sm:gap-4 sm:px-6"
                    >
                        <dt class="text-sm font-medium text-gray-900">
                            Roadbook autoscroll available
                            <span class="whitespace-nowrap">
                                in the app
                                <TooltipIcon
                                    href="/faq/what-is-the-autoscroll-37"
                                    classes="h-5"
                                >
                                    What is the "Autoscroll"?
                                </TooltipIcon>
                            </span>
                        </dt>
                        <dd
                            class="mt-1 text-sm font-semibold leading-6 sm:col-span-2 sm:mt-0"
                            :class="eventClass.is_autoscroll_allowed ? 'text-green-600' : 'text-red-600'"
                        >
                            {{ eventClass.is_autoscroll_allowed ? "YES" : "NO" }}
                        </dd>
                    </div>
                    <div
                        v-if="containsRoadbookSections(eventClass.classSections)"
                        class="p-4 sm:grid sm:grid-cols-1 sm:gap-4 sm:px-6"
                    >
                        <dt class="text-sm font-medium text-gray-900">
                            Roadbook "countdown style" available
                            <span class="whitespace-nowrap">
                                in the app
                                <TooltipIcon
                                    href="/faq/roadbook-countdown-style-38"
                                    classes="h-5"
                                >
                                    What is the roadbook "countdown style"?
                                </TooltipIcon>
                            </span>
                        </dt>
                        <dd
                            class="mt-1 text-sm font-semibold leading-6 sm:col-span-2 sm:mt-0"
                            :class="eventClass.force_dtw_classic ? 'text-red-600' : 'text-green-600'"
                        >
                            {{ eventClass.force_dtw_classic ? "NO" : "YES" }}
                        </dd>
                    </div>
                </dl>
            </div>
            <div
                v-if="eventClass.classSections.length > 0"
                class="flex-1 rounded border border-gray-100 bg-gray-50 p-4 text-sm"
            >
                <div
                    v-for="(classSection, classSectionIndex) in eventClass.classSections"
                    :key="classSectionIndex"
                >
                    <div class="px-2 py-4">
                        <div
                            v-if="classSection.track.visibility !== 'public'"
                            class="font-semibold"
                        >
                            {{ classSectionIndex + 1 }}. Section:
                            {{ classSection.name }}
                        </div>

                        <div
                            v-if="classSection.track.visibility === 'public'"
                            class="font-semibold"
                        >
                            {{ classSectionIndex + 1 }}. Section:
                            <a
                                :href="
                                    route('track.public.information', {
                                        pin: classSection.track.pin,
                                        seo_slug: classSection.track.seo_slug,
                                    })
                                "
                            >
                                {{ classSection.track.name }}
                            </a>
                        </div>
                    </div>
                    <div
                        v-if="isClassSectionProcessing(classSection)"
                        class="mx-4"
                    >
                        <div
                            class="inline-flex items-center gap-x-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600"
                        >
                            <SpinnerIcon class="size-4" />
                            Section is still processing, reload the page in a couple of seconds...
                        </div>
                    </div>
                    <div
                        v-else
                        class="mx-4"
                    >
                        <table class="table-auto">
                            <tr>
                                <td class="whitespace-nowrap align-top">Start at DSS:</td>
                                <td class="whitespace-nowrap">
                                    <FwbTooltip placement="right">
                                        <template #trigger>
                                            {{
                                                DateTime.fromISO(classSection.first_start_at)
                                                    .setZone($page.props.event.timezone)
                                                    .toLocaleString(DateTime.DATETIME_SHORT)
                                            }}
                                        </template>
                                        <template #content>
                                            In user time:
                                            {{ new Date(classSection.first_start_at).toLocaleString() }}
                                        </template>
                                    </FwbTooltip>
                                </td>
                            </tr>
                            <tr>
                                <td class="whitespace-nowrap align-top">Start type:</td>
                                <td>
                                    {{ $page.props.app.section_start_types[classSection.start_type].name }}
                                    <TooltipIcon
                                        href="/faq/section-start-types-standing-rolling-33"
                                        classes="h-5"
                                    >
                                        Section start types explained
                                    </TooltipIcon>
                                </td>
                            </tr>
                            <tr>
                                <td>Section type:</td>
                                <td>
                                    {{ $page.props.app.section_types[classSection.type].name }}
                                </td>
                            </tr>
                            <tr>
                                <td>Navigate by:</td>
                                <td>
                                    <NavigateByBadge :navigate-by="classSection.track.navigate_by" />
                                </td>
                            </tr>
                            <tr>
                                <td>Distance:</td>
                                <td>
                                    {{
                                        convertKmAsPerTrackUnits(
                                            classSection.trackVersion.distance / 1000,
                                            classSection.track.units,
                                            0,
                                        )
                                    }}
                                    {{ formatUnitsHuman(classSection.track.units) }}
                                    <span class="text-sm text-gray-400">
                                        {{ getTotalDistanceInClassUntilTrack(eventClass, classSection) }}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td class="whitespace-nowrap">Speed limit:</td>
                                <td>
                                    {{
                                        convertKmAsPerTrackUnits(
                                            classSection.speed_limit,
                                            classSection.track.units,
                                            0,
                                        )
                                    }}
                                    {{ formatSpeedUnitsHuman(classSection.track.units) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="whitespace-nowrap pr-2">
                                    Included in overall <br />
                                    event results:
                                </td>
                                <td>{{ classSection.is_add_to_overall_results ? "Yes" : "No" }}</td>
                            </tr>
                            <tr>
                                <td class="pr-2">Description:</td>
                                <td>
                                    {{ classSection.track.description ? classSection.track.description : "-" }}
                                </td>
                            </tr>
                            <tr>
                                <td class="pr-2">Start:</td>
                                <td class="whitespace-nowrap">
                                    <a
                                        :href="
                                            getDeviceNavigationAppUrl(
                                                classSection.trackVersion.start_location.coordinates,
                                            )
                                        "
                                        target="_blank"
                                    >
                                        {{
                                            classSection.trackVersion.start_location.coordinates
                                                .join(",")
                                                .split(",")
                                                .reverse()
                                                .join(", ")
                                        }}
                                        <ArrowTopRightOnSquareIcon
                                            class="inline size-5"
                                        ></ArrowTopRightOnSquareIcon>
                                    </a>
                                </td>
                            </tr>
                            <tr>
                                <td class="pr-2">End:</td>
                                <td class="whitespace-nowrap">
                                    <a
                                        :href="
                                            getDeviceNavigationAppUrl(
                                                classSection.trackVersion.end_location.coordinates,
                                            )
                                        "
                                        target="_blank"
                                    >
                                        {{
                                            classSection.trackVersion.end_location.coordinates
                                                .join(",")
                                                .split(",")
                                                .reverse()
                                                .join(", ")
                                        }}
                                        <ArrowTopRightOnSquareIcon
                                            class="inline size-5"
                                        ></ArrowTopRightOnSquareIcon>
                                    </a>
                                </td>
                            </tr>
                            <tr>
                                <td class="pr-2">Maximum time allowed:</td>
                                <td>
                                    {{
                                        Duration.fromMillis(
                                            classSection.max_time_allowed_m * 60 * 1000,
                                        ).toFormat("hh:mm")
                                    }}
                                    <TooltipIcon classes="h-5">
                                        <div class="max-w-2xl">
                                            Maximum individual driving time allowed (hh:mm) is set by the
                                            organizer (from DSS (start) to ASS (finish) waypoints). Maximum time
                                            allowed is given for each Selective Section (SS), and exceeding this
                                            time by more than 60 seconds, or not finishing the section within
                                            the maximum allowed time, will result in a 'Section Penalty'<br />
                                            <br />The Section Penalty is calculated by multiplying the section's
                                            maximum time allowed by the factor specified in the event penalties
                                            configuration. <br />
                                        </div>
                                    </TooltipIcon>
                                </td>
                            </tr>
                            <tr v-if="classSection.nominal_time_config.nominal_time_type">
                                <td class="pr-2">Nominal Time:</td>
                                <td>
                                    {{
                                        classSection.nominal_time_config.nominal_time_type === "fixed"
                                            ? "Fixed:"
                                            : ""
                                    }}
                                    {{
                                        classSection.nominal_time_config.nominal_time_type === "winner_plus"
                                            ? "Fastest clean time + "
                                            : ""
                                    }}
                                    {{ classSection.nominal_time_config.nominal_time_value }}
                                    <TooltipIcon
                                        classes="h-5"
                                        href="/faq/nominal-time-configuration-73"
                                    >
                                        <div class="max-w-2xl">
                                            Nominal Time is used to assign a standard result time to competitors
                                            who couldn't complete a section normally (DNS, DNF, or late finish
                                            scenarios). It ensures fairness by giving them a standard time and
                                            allows them to remain in the overall results. Click to learn more.
                                        </div>
                                    </TooltipIcon>
                                </td>
                            </tr>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
